/**
 * Workspace selection hook (v2.3.0).
 *
 * Provides current workspace, available workspaces (from switch-context), and
 * a manual switch function that calls Central directly.
 *
 * IMPORTANT — 2-step workspace switch:
 *   Step 1: POST {CENTRAL_URL}/api/auth/select-workspace  { workspaceId }
 *           → returns { accessToken, expiresIn } (new token scoped to new workspace)
 *   Step 2: GET  {CENTRAL_URL}/api/suite/switch-context    (Bearer <new token>)
 *           → returns { workspace, otherWorkspaces, entitlementsEffective }
 *
 * Why two steps?
 *   GET switch-context returns the context for whichever workspace the token is
 *   scoped to. It does NOT accept a workspaceId param. To switch, you must first
 *   get a new token via /api/auth/select-workspace, then call switch-context.
 *
 * All switching is manual (user-initiated) — no auto-switch.
 *
 * CRITICAL RULES (prevent login redirect during switch):
 *   - NEVER call centralLogout() during the switch flow.
 *   - NEVER navigate to /login during the switch flow.
 *   - isSwitchingWorkspace stays true until well after boot completes.
 *   - On error: show overlay (CentralUnavailable), NOT logout.
 *   - On 401: show error in overlay — user can retry or sign out manually.
 */

import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import {
  selectCurrentCentralWorkspace,
  selectOtherWorkspaces,
  selectIsCentralAuthenticated,
  selectCentralAccessToken,
  selectIsSwitchingWorkspace,
  setSwitchingWorkspace,
  setBootReady,
  setBootWorkspaceClosed,
  setBootUnavailable,
  updateCentralAccessToken,
} from '../store/slices/centralAuthSlice';
import { api } from '../store/api';
import { centralAuthApi } from '../store/api/centralAuthApi';
import { CENTRAL_URL, APP_ID } from '../config/centralAuth';

export function useWorkspace() {
  const dispatch = useDispatch<AppDispatch>();

  const currentWorkspace = useSelector(selectCurrentCentralWorkspace);
  const otherWorkspaces = useSelector(selectOtherWorkspaces);
  const isAuthenticated = useSelector(selectIsCentralAuthenticated);
  const accessToken = useSelector(selectCentralAccessToken);
  const isSwitching = useSelector(selectIsSwitchingWorkspace);

  // Combine current workspace + otherWorkspaces for the selector dropdown
  const allWorkspaces = currentWorkspace
    ? [currentWorkspace, ...otherWorkspaces.filter((ws) => ws.id !== currentWorkspace.id)]
    : [...otherWorkspaces];

  /**
   * Switch to a different workspace (manual, user-initiated only).
   *
   * Flow:
   * 1. Show blocking "Switching workspace…" overlay
   * 2. POST /api/auth/select-workspace → new token for target workspace
   * 3. GET  /api/suite/switch-context  → full context with new token
   * 4. On success: store context + token, reset caches, stay on current route
   * 5. On 401: return error (NO logout, NO login redirect)
   * 6. On 409: show WorkspaceClosedOverlay
   * 7. On error: show CentralUnavailableOverlay
   *
   * NEVER calls centralLogout(), navigate('/login'), or window.location.href.
   */
  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      if (!isAuthenticated || !accessToken) {
        return { success: false as const, error: 'Not authenticated' };
      }

      if (currentWorkspace?.id === workspaceId) {
        return { success: true as const };
      }

      // 1. Show blocking overlay (hides children → deactivates RTK Query subscriptions).
      //    This flag stays true until we explicitly clear it with a grace period.
      dispatch(setSwitchingWorkspace(true));

      try {
        // ── Step 1: Get new token scoped to target workspace ──
        const selectRes = await fetch(`${CENTRAL_URL}/api/auth/select-workspace`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ workspaceId }),
        });

        // 401 from select-workspace → token rejected by Central.
        // Do NOT logout. Return error so UI can show it. User can sign out manually.
        if (selectRes.status === 401) {
          dispatch(setSwitchingWorkspace(false));
          return { success: false as const, error: 'Session expired — please sign out and log in again' };
        }

        if (!selectRes.ok) {
          const errData = await selectRes.json().catch(() => ({}));
          console.error('[useWorkspace] select-workspace failed:', selectRes.status, errData);
          dispatch(setBootUnavailable(`Switch failed (${selectRes.status})`));
          dispatch(setSwitchingWorkspace(false));
          return { success: false as const, error: `Switch failed (${selectRes.status})` };
        }

        const selectData = await selectRes.json();
        const newToken: string = selectData.accessToken;
        const newExpiresIn: number = selectData.expiresIn ?? 3600;

        if (!newToken) {
          console.error('[useWorkspace] select-workspace returned no accessToken');
          dispatch(setBootUnavailable('No token returned'));
          dispatch(setSwitchingWorkspace(false));
          return { success: false as const, error: 'No token returned' };
        }

        // Store new token immediately (single source of truth)
        dispatch(updateCentralAccessToken({ accessToken: newToken, expiresIn: newExpiresIn }));

        // ── Step 2: Get full context with the new token ──
        const contextRes = await fetch(`${CENTRAL_URL}/api/suite/switch-context`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${newToken}`,
            'X-App-Id': APP_ID,
          },
        });

        if (contextRes.ok) {
          const ctxData = await contextRes.json();
          // If switch-context returns yet another token, use it
          if (ctxData.accessToken) {
            dispatch(updateCentralAccessToken({
              accessToken: ctxData.accessToken,
              expiresIn: ctxData.expiresIn,
            }));
          }

          // Clear workspace-scoped caches NOW (children are hidden behind
          // the switching overlay, so no active subscriptions will re-fire).
          dispatch(api.util.resetApiState());
          dispatch(centralAuthApi.util.resetApiState());

          // Store new context (does NOT clear isSwitchingWorkspace).
          dispatch(
            setBootReady({
              currentWorkspace: ctxData.workspace,
              otherWorkspaces: ctxData.otherWorkspaces ?? [],
              apps: ctxData.apps ?? [],
              user: ctxData.user ?? null,
              meta: ctxData.meta,
            })
          );

          // Stay on current route — no navigate('/dashboard'), no reload.
          // Clear the switching guard with a grace period so post-switch
          // RTK Query re-fetches don't race with baseQueryWithReauth.
          setTimeout(() => {
            dispatch(setSwitchingWorkspace(false));
          }, 1500);

          return { success: true as const };
        }

        // 401 on switch-context — token rejected. Do NOT logout.
        if (contextRes.status === 401) {
          dispatch(setSwitchingWorkspace(false));
          return { success: false as const, error: 'Session expired — please sign out and log in again' };
        }

        // 409 WORKSPACE_CLOSED
        if (contextRes.status === 409) {
          const data = await contextRes.json().catch(() => ({}));
          if (data.code === 'WORKSPACE_CLOSED') {
            dispatch(setBootWorkspaceClosed({ otherWorkspaces: data.otherWorkspaces ?? [] }));
            dispatch(setSwitchingWorkspace(false));
            return { success: false as const, error: 'Workspace closed' };
          }
        }

        // Other errors → CentralUnavailable overlay (user retries manually)
        dispatch(setBootUnavailable(`Context load failed (${contextRes.status})`));
        dispatch(setSwitchingWorkspace(false));
        return { success: false as const, error: `Context load failed (${contextRes.status})` };
      } catch (error) {
        console.error('[useWorkspace] Switch error:', error);
        dispatch(setBootUnavailable('Network error'));
        dispatch(setSwitchingWorkspace(false));
        return { success: false as const, error: 'Network error' };
      }
    },
    [isAuthenticated, accessToken, currentWorkspace, dispatch]
  );

  return {
    currentWorkspace,
    workspaces: allWorkspaces,
    otherWorkspaces,
    switchWorkspace,
    isLoading: isSwitching,
    hasMultipleWorkspaces: allWorkspaces.length > 1,
  };
}
